/**
 * Mix & Match configurator for double-sided backdrops.
 *
 *  <mix-match-picker>  three collapsible steps (size & material, front, back). Step 1 adopts the
 *                      theme's own variant picker (.option-selectors) so Canopy's clicky boxes,
 *                      price and availability logic keep working. Steps 2/3 write the chosen
 *                      designs into hidden line item property inputs bound to the product form.
 *  <mix-match-board>   the live preview in the gallery column: faces, flip, size/material.
 *
 * Markup comes from snippets/product-mix-match-picker.liquid and product-mix-match-board.liquid.
 */
(function () {
  const SIZE_RE = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i;

  function variantFromForm(form) {
    // Read the selected variant from the product JSON the theme already prints.
    const select = form && form.querySelector('[name="id"]');
    const productId = form && form.dataset.productId;
    const jsonEl = productId && document.getElementById('ccProductJson-' + productId);
    if (!select || !jsonEl) return null;
    try {
      const product = JSON.parse(jsonEl.textContent);
      return product.variants.find((v) => String(v.id) === String(select.value)) || null;
    } catch (e) {
      return null;
    }
  }

  function describeVariant(variant) {
    if (!variant) return { size: null, material: null, isVinyl: false, title: '' };
    const values = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    const sizeValue = values.find((v) => SIZE_RE.test(v)) || null;
    const m = sizeValue && sizeValue.match(SIZE_RE);
    const material = values.find((v) => v !== sizeValue) || null;
    return {
      size: m ? { w: parseFloat(m[1]), h: parseFloat(m[2]), label: sizeValue } : null,
      material,
      isVinyl: values.some((v) => /vinyl/i.test(v)),
      title: values.join(' / '),
    };
  }

  // Canopy binds its option/variant handlers on its own bundled jQuery (theme.jQuery), which is
  // not necessarily window.jQuery, so always talk to the theme through that instance.
  function themeJQ() {
    return (window.theme && theme.jQuery) || window.jQuery || null;
  }

  // The material label sits on top of the artwork, so it has to flip to white over a dark
  // design. The swatch images are served from the shop's own domain, so the canvas is not
  // tainted and the pixels can be read back. Sampled once per image and cached.
  function hexLuminance(hex) {
    const m = String(hex).trim().replace('#', '');
    const full = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
    if (!/^[0-9a-f]{6}$/i.test(full)) return 255;
    const n = parseInt(full, 16);
    return 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255);
  }

  const luminanceCache = new Map();
  function measureLuminance(url) {
    if (!url) return Promise.resolve(null);
    if (luminanceCache.has(url)) return Promise.resolve(luminanceCache.get(url));
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        let value = null;
        try {
          const size = 32;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, size, size);
          // only the middle of the image matters - that is where the label sits
          const inset = Math.round(size * 0.3);
          const data = ctx.getImageData(inset, inset, size - inset * 2, size - inset * 2).data;
          let total = 0;
          for (let i = 0; i < data.length; i += 4) {
            total += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
          }
          value = total / (data.length / 4);
        } catch (e) {
          value = null; // tainted canvas or decode failure: leave the default styling
        }
        luminanceCache.set(url, value);
        resolve(value);
      };
      img.onerror = () => { luminanceCache.set(url, null); resolve(null); };
      img.src = url;
    });
  }

  function formatMoney(cents) {
    if (window.theme && typeof theme.formatMoney === 'function') return theme.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  // ---------------------------------------------------------------- board
  if (!customElements.get('mix-match-board')) {
    class MixMatchBoard extends HTMLElement {
      connectedCallback() {
        this.board = this.querySelector('[data-mm-board]');
        this.faces = { front: this.querySelector('[data-mm-face="front"]'), back: this.querySelector('[data-mm-face="back"]') };
        this.staticFaces = { front: this.querySelector('[data-mm-face-static="front"]'), back: this.querySelector('[data-mm-face-static="back"]') };
        this.flipBtn = this.querySelector('[data-mm-flip]');
        this.caption = this.querySelector('[data-mm-caption]');
        this.flipped = false;
        this.orientation = { front: 'portrait', back: 'portrait' };
        this.baseCaption = this.caption ? this.caption.textContent : '';

        if (this.flipBtn) this.flipBtn.addEventListener('click', () => this.flip(!this.flipped));
        this.querySelectorAll('[data-mm-chip]').forEach((chip) => {
          chip.addEventListener('click', () => {
            this.dispatchEvent(new CustomEvent('mix-match:open-side', { bubbles: true, detail: { side: chip.dataset.mmChip } }));
          });
        });

        // The board scales against the biggest size on offer, so every size keeps its real
        // proportions relative to the others rather than all sharing one width.
        this.maxEdge = (this.dataset.sizes || '')
          .split(',')
          .map((v) => v.match(SIZE_RE))
          .filter(Boolean)
          .reduce((max, m) => Math.max(max, parseFloat(m[1]), parseFloat(m[2])), 0);

        const initial = this.dataset.size && this.dataset.size.match(SIZE_RE);
        if (initial) this.setSize(parseFloat(initial[1]), parseFloat(initial[2]));
        if (this.dataset.mode !== 'split') this.initDrag();
      }

      // Click-and-drag (or swipe) spins the board; on release it settles on whichever side faces the viewer.
      initDrag() {
        const scene = this.querySelector('.mix-match-scene');
        if (!scene || !this.board) return;
        let startX = 0;
        let drag = 0;
        let active = false;
        const setDrag = (deg) => this.board.style.setProperty('--mm-drag', `${deg}deg`);

        scene.addEventListener('pointerdown', (e) => {
          if (e.button !== 0 || this.flippable === false) return;
          active = true;
          drag = 0;
          startX = e.clientX;
          scene.classList.add('is-dragging');
          this.board.classList.add('is-dragging');
          scene.setPointerCapture(e.pointerId);
        });
        scene.addEventListener('pointermove', (e) => {
          if (!active) return;
          drag = ((e.clientX - startX) / Math.max(scene.clientWidth, 1)) * 220;
          setDrag(drag);
        });
        const release = (e) => {
          if (!active) return;
          active = false;
          scene.classList.remove('is-dragging');
          if (Math.abs(drag) < 2) { this.board.classList.remove('is-dragging'); setDrag(0); return; }

          // Which side is facing the viewer now?
          const base = (this.flipped ? 180 : 0) - 16;
          const current = base + drag;
          const norm = ((current + 180) % 360 + 360) % 360 - 180; // -180..180
          const toBack = Math.abs(norm - -16) > 90;
          const newBase = (toBack ? 180 : 0) - 16;
          let rest = current - newBase;
          rest = ((rest + 180) % 360 + 360) % 360 - 180; // shortest path back to rest

          // keep the same visual angle while switching the flip state (no transition yet)...
          this.flipped = toBack;
          this.board.classList.toggle('is-back', toBack);
          setDrag(rest);
          void this.board.offsetWidth;
          // ...then animate to rest
          this.board.classList.remove('is-dragging');
          setDrag(0);
          if (this.flipBtn) this.flipBtn.textContent = toBack ? this.flipBtn.dataset.labelFront || 'Show front' : this.flipBtn.dataset.labelBack || 'Show back';
          if (e.pointerId !== undefined) { try { scene.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ } }
        };
        scene.addEventListener('pointerup', release);
        scene.addEventListener('pointercancel', release);
        scene.addEventListener('lostpointercapture', release);
      }

      setSize(w, h) {
        if (!w || !h) return;
        this.size = { w, h };
        this.applyAspect();
      }

      // A board is a flat panel, so a landscape design (RoomScapes, Bokeh) is the same product
      // turned on its side: a 24x36 board becomes 36x24. Each side keeps its own orientation:
      // the flipping board follows whichever side is showing, the split boards follow their own.
      applyAspect() {
        // Products without a Size option (the original per-size products) never report
        // dimensions, so fall back to the artwork's own proportions rather than leaving the
        // board at its portrait default and cropping every landscape design.
        const size = this.size;
        const long = size ? Math.max(size.w, size.h) : 3;
        const short = size ? Math.min(size.w, size.h) : 2;
        const maxEdge = this.maxEdge || long;

        // Rendered dimensions for a side, in inches: a landscape design is the same panel
        // turned on its side, so the long edge becomes the horizontal one.
        const dims = (side) => (this.orientation[side] === 'landscape' ? [long, short] : [short, long]);

        const apply = (el, side) => {
          if (!el) return;
          const [w, h] = dims(side);
          el.style.setProperty('--mm-ar', `${w} / ${h}`);
          // --mm-scale is the horizontal edge as a fraction of the largest edge, so the
          // biggest size fills the frame and the rest shrink in proportion.
          el.style.setProperty('--mm-scale', size ? (w / maxEdge).toFixed(4) : '1');
        };

        apply(this.board, this.flipped ? 'back' : 'front');
        ['front', 'back'].forEach((side) => apply(this.querySelector(`[data-mm-board-static="${side}"]`), side));
        this.applyDimLabels(dims);
      }

      // Measurements beside the board, following the size and each side's orientation.
      applyDimLabels(dims) {
        const current = this.flipped ? 'back' : 'front';
        const label = (n) => `${+n.toFixed(2)}"`;
        [['[data-mm-dim-w]', 'mmDimW', 0], ['[data-mm-dim-h]', 'mmDimH', 1]].forEach(([sel, key, i]) => {
          this.querySelectorAll(sel).forEach((el) => {
            const side = el.dataset[key] || current;
            el.textContent = this.size ? label(dims(side)[i]) : '';
          });
        });
      }

      setMaterial(isVinyl, label, material) {
        // Every face carries its own label - the flipping board's front and back, and both
        // split boards - so they all have to be filled, not just the first one.
        this.querySelectorAll('[data-mm-material]').forEach((el) => {
          el.textContent = material || '';
        });
        this.querySelectorAll('.mix-match-board').forEach((b) => b.classList.toggle('mix-match-board--vinyl', !!isVinyl));
        this.classList.toggle('mix-match-stage--vinyl', !!isVinyl);
        if (this.caption && label && this.caption.dataset.mmCaptionTemplate) {
          this.caption.textContent = this.caption.dataset.mmCaptionTemplate.replace('[variant]', label);
        }
      }

      setVariant(variant) {
        const d = describeVariant(variant);
        if (d.size) this.setSize(d.size.w, d.size.h);
        this.setMaterial(d.isVinyl, d.title, d.material);
      }

      setFace(side, image, label, orientation) {
        if (image) this.stopDemo();
        this.orientation[side] = orientation === 'landscape' ? 'landscape' : 'portrait';
        this.applyAspect();
        [this.faces[side], this.staticFaces[side]].forEach((el) => {
          if (!el) return;
          if (image) {
            el.style.backgroundImage = `url("${image}")`;
            el.classList.add('has-image');
            measureLuminance(image).then((lum) => {
              if (lum === null) return;
              el.classList.toggle('is-dark-art', lum < 128);
            });
          } else {
            el.style.backgroundImage = '';
            el.classList.remove('has-image', 'is-dark-art');
          }
        });
        const chip = this.querySelector(`[data-mm-chip="${side}"]`);
        if (chip) {
          chip.classList.toggle('is-done', !!image);
          const value = chip.querySelector('[data-mm-chip-value]');
          if (value) value.textContent = label || value.dataset.empty || value.textContent;
        }
      }

      // Custom-colour products paint the face instead of putting artwork on it. Same
      // board, same size/material/flip behaviour - only the surface differs.
      setFaceColor(side, hex, label) {
        const clean = (hex || '').trim();
        if (clean) this.stopDemo();
        [this.faces[side], this.staticFaces[side]].forEach((el) => {
          if (!el) return;
          el.style.backgroundImage = '';
          if (clean) {
            el.style.backgroundColor = clean;
            el.classList.add('has-image');
            // a dark colour needs the light label, same rule as dark artwork
            el.classList.toggle('is-dark-art', hexLuminance(clean) < 128);
          } else {
            el.style.backgroundColor = '';
            el.classList.remove('has-image', 'is-dark-art');
          }
        });
        const chip = this.querySelector(`[data-mm-chip="${side}"]`);
        if (chip) {
          chip.classList.toggle('is-done', !!clean);
          const value = chip.querySelector('[data-mm-chip-value]');
          if (value) value.textContent = label || clean || value.dataset.empty || value.textContent;
        }
      }

      // ----- demo -------------------------------------------------------------
      // The demo lives inside the board, so the board is the one place that sees every
      // real choice: Mix & Match and custom colour use different pickers, and wiring this
      // per picker would have left the Mix & Match demo running over a chosen design.
      stopDemo() {
        const demo = this.querySelector('mix-match-ambient');
        if (demo && demo.stop) demo.stop();
      }

      // Cosmetic orientation, so a landscape design previews on a board turned on its side
      // rather than cropped into a portrait panel. A side the shopper has already filled
      // keeps whatever setFace gave it.
      setDemoOrientation(side, orientation) {
        const face = this.faces[side];
        if (face && face.classList.contains('has-image')) return;
        this.orientation[side] = orientation === 'landscape' ? 'landscape' : 'portrait';
        this.applyAspect();
      }

      clearDemoOrientation() {
        ['front', 'back'].forEach((side) => {
          const face = this.faces[side];
          if (face && face.classList.contains('has-image')) return;
          this.orientation[side] = 'portrait';
        });
        this.applyAspect();
      }

      setCurrent(side) {
        this.querySelectorAll('[data-mm-chip]').forEach((chip) => chip.setAttribute('aria-current', chip.dataset.mmChip === side ? 'true' : 'false'));
        if (side === 'front' || side === 'back') this.flip(side === 'back');
      }

      // A single sided product has nothing on the back, so the board must not turn -
      // neither by the button nor by dragging it.
      setFlippable(canFlip) {
        this.flippable = canFlip !== false;
        this.classList.toggle('mix-match-stage--no-flip', !this.flippable);
        if (this.flipBtn) this.flipBtn.hidden = !this.flippable;
        if (!this.flippable) this.flip(false);
      }

      flip(toBack) {
        if (this.flippable === false && toBack) return;
        this.flipped = !!toBack;
        if (this.board) this.board.classList.toggle('is-back', this.flipped);
        this.applyAspect();
        if (this.flipBtn) this.flipBtn.textContent = this.flipped ? this.flipBtn.dataset.labelFront || 'Show front' : this.flipBtn.dataset.labelBack || 'Show back';
      }
    }
    customElements.define('mix-match-board', MixMatchBoard);
  }

  // ---------------------------------------------------------------- picker
  if (customElements.get('mix-match-picker')) return;

  class MixMatchPicker extends HTMLElement {
    connectedCallback() {
      this.prefill = this.readPrefill();
      this.form = document.getElementById(this.dataset.formId);
      this.container = this.closest('.product-container');
      this.board = this.container && this.container.querySelector('mix-match-board');
      this.confirmStep = this.dataset.confirm === 'true';
      this.steps = Array.from(this.querySelectorAll('[data-mm-step]'));
      this.sides = this.steps.filter((s) => s.dataset.mmSide);
      this.modal = this.querySelector('[data-mm-modal]');

      // remember the "empty" labels so we can restore them
      this.querySelectorAll('[data-mm-step-value]').forEach((el) => {
        el.dataset.empty = el.textContent;
      });
      if (this.board) {
        this.board.querySelectorAll('[data-mm-chip-value]').forEach((el) => { el.dataset.empty = el.textContent; });
      }

      this.steps.forEach((step) => this.initStep(step));
      this.sides.forEach((side) => this.initSide(side));
      if (this.dataset.adoptVariants === 'true') this.adoptVariantPicker();
      this.initModal();
      this.retireInfiniteOptions();

      if (this.form && this.form.mixMatchPicker !== this) {
        // Capture phase so this runs before the theme's ajax submit handler on the form.
        this.boundSubmit = (event) => this.onSubmit(event);
        this.form.addEventListener('submit', this.boundSubmit, true);
        this.form.mixMatchPicker = this;
      }
      if (this.board) {
        this.board.addEventListener('mix-match:open-side', (e) => this.openStep(e.detail.side));
      }

      this.applyPrefill();

      // open the first step
      const first = this.steps[0];
      this.openStep(first ? first.dataset.mmStep : null, { scroll: false });
    }

    // Arrivals from a single-sided product's "Build it double-sided" link carry their
    // choices as query params. Size and material are applied to the theme's own variant
    // selects; the design is matched by NAME against the swatches, so no metafield has to
    // be kept on every single-sided product. Around a third of that catalogue has no
    // Mix & Match equivalent, so a design that does not match is simply left unchosen.
    readPrefill() {
      let params;
      try { params = new URLSearchParams(window.location.search); } catch (e) { return null; }
      const size = params.get('mm_size');
      const material = params.get('mm_material');
      const design = params.get('mm_design');
      if (size || material || design) return { size, material, design, variantApplied: false };

      // Arriving cold, the product would otherwise open on whichever variant happens to be
      // first. Only applied when the shopper has not asked for a variant themselves.
      const fallback = (this.dataset.defaultSize || '').trim();
      if (fallback && !params.get('variant')) {
        return { size: fallback, material: null, design: null, variantApplied: false };
      }
      return null;
    }

    // Size and material have to wait for Canopy to finish wiring its own variant picker;
    // a change event dispatched before that is simply not heard, which is why the board
    // stayed on the default variant while the design prefilled correctly.
    applyVariantPrefill(attempt) {
      attempt = attempt || 0;
      const p = this.prefill;
      if (!p || p.variantApplied || (!p.size && !p.material)) return;

      const retry = () => {
        if (attempt >= 5) return;
        window.setTimeout(() => this.applyVariantPrefill(attempt + 1), [0, 150, 400, 900, 1600][attempt]);
      };

      // adoptVariantPicker moves .option-selectors into this component, so the selects are
      // no longer inside the product form - look for them where they actually live.
      const host = (this.container || document).querySelector('.option-selectors') || this.form;
      const selects = host
        ? Array.from(host.querySelectorAll('select')).filter((el) => el.name !== 'id')
        : [];
      if (!selects.length) return retry();

      const norm = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const idInput = this.form && this.form.querySelector('[name="id"]');
      const before = idInput ? idInput.value : null;

      // Set every value BEFORE announcing the change. Canopy looks the variant up from the
      // whole combination and dereferences it without a guard, so firing per select walks
      // through combinations that may not exist (Vinyl + 24x24) and throws part way.
      let last = null;
      [p.material, p.size].forEach((wanted) => {
        if (!wanted) return;
        selects.forEach((select) => {
          const match = Array.from(select.options).find((o) => norm(o.value) === norm(wanted));
          if (!match) return;
          select.value = match.value;
          last = select;
        });
      });
      if (!last) return;

      const jq = themeJQ();
      if (jq) jq(last).trigger('change');
      else last.dispatchEvent(new Event('change', { bubbles: true }));

      // Canopy binds its own change handler during initProductOptions, which can land after
      // this runs; a change fired before that is simply not heard. Confirm the variant
      // actually moved and try again if it did not.
      if (idInput && idInput.value === before) return retry();
      p.variantApplied = true;
    }

    applyPrefill() {
      const p = this.prefill;
      if (!p) return;
      const design = p.design;
      const norm = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      this.applyVariantPrefill();

      if (design) {
        const wanted = norm(design);
        const radios = Array.from(this.querySelectorAll('[data-mm-design]'));
        // exact first, then either name containing the other, since Mix & Match designs
        // carry the influencer prefix ("Emma Dunham Honey Rattan") and product titles
        // may or may not.
        const radio =
          radios.find((r) => norm(r.value) === wanted) ||
          radios.find((r) => norm(r.value).endsWith(wanted)) ||
          radios.find((r) => wanted.endsWith(norm(r.value)));
        if (radio) {
          const side = radio.closest('[data-mm-side]');
          if (side) {
            const panel = radio.closest('[data-mm-panel]');
            if (panel && side.showPanel) side.showPanel(panel.dataset.mmPanel);
            radio.checked = true;
            this.select(side, radio, { silent: true });
          }
        }
      }
    }

    // This picker replaces the Infinite Options app for the product it's on. If the app still
    // renders its own required "Pick a Category" fields (e.g. the product is in one of its option
    // sets, or its app block is still in the template), they would block the add-to-cart, so
    // remove its container and keep it removed if the app re-injects it.
    retireInfiniteOptions() {
      const scope = this.container || document;
      const purge = () => scope.querySelectorAll('#infiniteoptions-container, .infiniteoptions-container').forEach((el) => el.remove());
      purge();
      this.ioObserver = new MutationObserver(purge);
      this.ioObserver.observe(scope, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      if (this.ioObserver) this.ioObserver.disconnect();
      if (this.boxObserver) this.boxObserver.disconnect();
      if (this.form && this.boundSubmit) {
        this.form.removeEventListener('submit', this.boundSubmit, true);
        if (this.form.mixMatchPicker === this) delete this.form.mixMatchPicker;
      }
      if (this.variantHandler && themeJQ()) themeJQ()(window).off('cc-variant-updated', this.variantHandler);
    }

    // ----- steps
    initStep(step) {
      step.querySelector('[data-mm-step-toggle]').addEventListener('click', () => {
        if (step.classList.contains('is-locked')) return;
        this.openStep(step.dataset.mmStep === this.openName ? null : step.dataset.mmStep);
      });
      const cont = step.querySelector('[data-mm-continue]');
      if (cont) cont.addEventListener('click', () => { step.dataset.mmDone = 'true'; this.advanceFrom(step.dataset.mmStep); });
    }

    stepDone(step) {
      if (step.dataset.mmSide) {
        const input = step.querySelector('[data-mm-property]');
        const chosen = !!(input && !input.disabled && input.value);
        return this.confirmStep ? step.dataset.mmDone === 'true' && chosen : chosen;
      }
      return step.dataset.mmDone === 'true';
    }

    openStep(name, opts) {
      this.openName = name;
      let prevDone = true;
      this.steps.forEach((step) => {
        const isOpen = step.dataset.mmStep === name;
        const done = this.stepDone(step);
        const locked = !prevDone && !done && !isOpen;
        step.classList.toggle('is-open', isOpen);
        step.classList.toggle('is-done', done);
        step.classList.toggle('is-locked', locked);
        step.querySelector('[data-mm-step-toggle]').setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        step.querySelector('[data-mm-step-body]').hidden = !isOpen;
        const edit = step.querySelector('[data-mm-step-edit]');
        if (edit) edit.hidden = !(done && !isOpen);
        prevDone = done;
      });
      if (this.board) this.board.setCurrent(name);
      if (name && !(opts && opts.scroll === false)) {
        const el = this.steps.find((s) => s.dataset.mmStep === name);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    goBackFrom(name) {
      const idx = this.steps.findIndex((s) => s.dataset.mmStep === name);
      const prev = this.steps[idx - 1];
      if (prev) this.openStep(prev.dataset.mmStep);
    }

    advanceFrom(name) {
      const idx = this.steps.findIndex((s) => s.dataset.mmStep === name);
      const next = this.steps.slice(idx + 1).find((s) => !this.stepDone(s));
      if (next) {
        this.openStep(next.dataset.mmStep);
      } else {
        this.openStep(null);
        const submit = this.form && this.form.querySelector('[type="submit"]');
        if (submit) submit.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    // ----- step 1: adopt the theme's variant picker
    adoptVariantPicker() {
      const slot = this.querySelector('[data-mm-variant-slot]');
      const picker = this.container && this.container.querySelector('.option-selectors');
      if (!slot) return;
      const sizeStep = this.steps.find((s) => s.dataset.mmStep === 'size');
      if (!picker) {
        // Single-variant product (or the Variant picker block was removed): nothing to choose here.
        if (sizeStep) {
          sizeStep.hidden = true;
          sizeStep.dataset.mmDone = 'true';
          this.steps = this.steps.filter((s) => s !== sizeStep);
        }
        return;
      }
      if (!slot.contains(picker)) slot.appendChild(picker);

      // Hide option values that don't exist for the values chosen before them
      // (e.g. no 24x24 in Vinyl), and auto-correct the selection if it just disappeared.
      let filtering = false;
      const optionKind = (select) => {
        const label = select.closest('.selector-wrapper')?.querySelector('label');
        const text = (label?.textContent || '').replace(':', '').trim().toLowerCase();
        if (text === 'material') return 'material';
        if (text === 'size') return 'size';
        const id = (select.id || '').toLowerCase();
        if (id.includes('material')) return 'material';
        if (id.includes('size')) return 'size';
        return 'other';
      };

      const variantExistsForOption = (product, optionIndex, optionValue, selectedValues) =>
        product.variants.some((v) => {
          if (!v.available) return false;
          if (v.options[optionIndex] !== optionValue) return false;
          for (let oi = 0; oi < selectedValues.length; oi++) {
            if (oi === optionIndex) continue;
            if (v.options[oi] !== selectedValues[oi]) return false;
          }
          return true;
        });

      const materialOptionExists = (product, optionIndex, optionValue) =>
        product.variants.some((v) => v.available && v.options[optionIndex] === optionValue);

      const filterOptions = () => {
        if (filtering || !this.form) return;
        const productId = this.form.dataset.productId;
        const jsonEl = productId && document.getElementById('ccProductJson-' + productId);
        if (!jsonEl) return;
        let product;
        try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }
        const selects = Array.from(picker.querySelectorAll('select')).filter((s) => s.name !== 'id');
        if (selects.length < 2) return;
        filtering = true;
        try {
          let correctedSelect = null;
          selects.forEach((select, index) => {
            const selectedValues = selects.map((s) => s.value);
            const kind = optionKind(select);
            let firstVisible = null;
            let currentHidden = false;
            Array.from(select.options).forEach((opt) => {
              const exists = kind === 'material'
                ? materialOptionExists(product, index, opt.value)
                : variantExistsForOption(product, index, opt.value, selectedValues);
              opt.hidden = !exists;
              const box = picker.querySelector(`#clickyboxes-${CSS.escape(select.id)} li a[data-value="${CSS.escape(opt.value)}"]`);
              if (box) {
                box.parentElement.hidden = !exists;
                box.parentElement.style.display = exists ? '' : 'none';
              }
              if (exists && firstVisible === null) firstVisible = opt.value;
              if (!exists && select.value === opt.value) currentHidden = true;
            });
            if (currentHidden && firstVisible !== null) {
              select.value = firstVisible;
              correctedSelect = select;
            }
          });
          if (correctedSelect) {
            const jq = themeJQ();
            if (jq) jq(correctedSelect).trigger('change');
            else correctedSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } finally {
          filtering = false;
        }
      };

      const update = (variant) => {
        filterOptions();
        const v = variant || variantFromForm(this.form);
        const step = this.steps.find((s) => s.dataset.mmStep === 'size');
        if (step) {
          const value = step.querySelector('[data-mm-step-value]');
          if (v) {
            const d = describeVariant(v);
            value.textContent = `${d.title} · ${formatMoney(v.price)}`;
          } else {
            value.textContent = value.dataset.empty;
          }
          const cont = step.querySelector('[data-mm-continue]');
          if (cont) cont.disabled = !v || v.available === false;
        }
        if (this.board) this.board.setVariant(v);
      };

      const jq = themeJQ();
      if (jq) {
        this.variantHandler = (e, data) => update(data && data.variant);
        jq(window).on('cc-variant-updated', this.variantHandler);
      }
      if (this.form) {
        const select = this.form.querySelector('[name="id"]');
        if (select) select.addEventListener('change', () => update(null));
      }
      picker.addEventListener('change', (event) => {
        if (event.target instanceof HTMLSelectElement && event.target.name !== 'id') filterOptions();
      }, true);

      update(null);
      if (picker.querySelector('.clickyboxes')) this.applyVariantPrefill();

      // Canopy builds its clicky boxes from the <select>s after this component has
      // initialised, so that first pass hides the <option>s but finds no <li> to hide
      // alongside them: every size stayed visible until the shopper changed material.
      // Re-run the filter once the boxes exist.
      if (!picker.querySelector('.clickyboxes')) {
        this.boxObserver = new MutationObserver(() => {
          if (!picker.querySelector('.clickyboxes')) return;
          this.boxObserver.disconnect();
          this.boxObserver = null;
          this.applyVariantPrefill();
          filterOptions();
        });
        this.boxObserver.observe(picker, { childList: true, subtree: true });
      }
    }

    // ----- steps 2/3
    initSide(side) {
      const panels = Array.from(side.querySelectorAll('[data-mm-panel]'));
      const showPanel = (handle) => {
        panels.forEach((panel) => { panel.hidden = panel.dataset.mmPanel !== handle; });
        side.querySelectorAll('[data-mm-category-tab]').forEach((tab) => tab.setAttribute('aria-selected', tab.dataset.mmCategoryTab === handle ? 'true' : 'false'));
        const select = side.querySelector('[data-mm-category]');
        if (select && select.value !== handle) select.value = handle;
      };
      side.showPanel = showPanel;

      const select = side.querySelector('[data-mm-category]');
      if (select) select.addEventListener('change', () => showPanel(select.value));
      side.querySelectorAll('[data-mm-category-tab]').forEach((tab) => tab.addEventListener('click', () => showPanel(tab.dataset.mmCategoryTab)));

      side.addEventListener('change', (event) => {
        if (event.target.matches('[data-mm-design]')) this.select(side, event.target);
      });

      side.querySelectorAll('[data-mm-nav]').forEach((button) => {
        button.addEventListener('click', () => {
          const track = button.closest('[data-mm-panel]').querySelector('.mix-match-swatches');
          if (!track) return;
          const direction = button.dataset.mmNav === 'prev' ? -1 : 1;
          track.scrollBy({ left: direction * Math.max(track.clientWidth * 0.82, 180), behavior: 'smooth' });
        });
      });

      side.querySelectorAll('[data-mm-expand]').forEach((button) => button.addEventListener('click', () => this.openModal(side, button.dataset.mmExpand)));

      const confirm = side.querySelector('[data-mm-confirm]');
      if (confirm) confirm.addEventListener('click', () => this.confirm(side));
      if (!this.confirmStep && confirm) confirm.hidden = true;
      const back = side.querySelector('[data-mm-back]');
      if (back) back.addEventListener('click', () => this.goBackFrom(side.dataset.mmSide));

      // Restore a selection the browser kept (back/forward cache).
      const checked = side.querySelector('[data-mm-design]:checked');
      if (checked) this.select(side, checked, { silent: true });
    }

    select(side, radio, opts) {
      const name = side.dataset.mmSide;
      const category = radio.dataset.mmCategoryName || '';
      const prefix = side.dataset.mmPropertyPrefix || '';

      // Mirror the Infinite Options app's property names exactly:
      // "_Front Side Category" = category, "Front Side - <Category>" = design.
      const input = side.querySelector('[data-mm-property]');
      input.name = category ? `properties[${prefix} - ${category}]` : `properties[${prefix}]`;
      input.value = radio.value;
      input.disabled = false;
      const categoryInput = side.querySelector('[data-mm-property-category]');
      if (categoryInput) { categoryInput.value = category; categoryInput.disabled = !category; }

      side.dataset.mmDone = this.confirmStep ? 'false' : 'true';

      // step summary (the board shows the design itself; no separate preview bar)
      const stepValue = side.querySelector('[data-mm-step-value]');
      if (stepValue) stepValue.textContent = category ? `${radio.value} · ${category}` : radio.value;
      const stepThumb = side.querySelector('[data-mm-step-thumb]');
      if (stepThumb) {
        stepThumb.hidden = !radio.dataset.mmImage;
        if (radio.dataset.mmImage) stepThumb.src = radio.dataset.mmImage;
        const landscape = radio.dataset.mmOrientation === 'landscape';
        stepThumb.classList.toggle('mix-match-step__thumb--landscape', landscape);
        stepThumb.width = landscape ? 46 : 32;
        stepThumb.height = landscape ? 32 : 46;
      }
      const confirm = side.querySelector('[data-mm-confirm]');
      if (confirm) confirm.disabled = false;

      this.setError(side, '');
      if (this.board) {
        this.board.setFace(name, radio.dataset.mmImageLarge || radio.dataset.mmImage || '', radio.value, radio.dataset.mmOrientation);
        this.board.flip(name === 'back');
      }
      this.dispatchEvent(new CustomEvent('mix-match:change', { bubbles: true, detail: { side: name, value: radio.value, category } }));

      if (!this.confirmStep && !(opts && opts.silent)) {
        window.setTimeout(() => this.advanceFrom(name), 600);
      } else if (!(opts && opts.silent)) {
        this.openStep(this.openName, { scroll: false }); // refresh done/locked classes
      }
    }

    confirm(side) {
      const input = side.querySelector('[data-mm-property]');
      if (!input || input.disabled || !input.value) return;
      side.dataset.mmDone = 'true';
      this.advanceFrom(side.dataset.mmSide);
    }

    setError(side, message) {
      const el = side.querySelector('[data-mm-error-message]');
      side.classList.toggle('mix-match-step--error', !!message);
      if (el) { el.textContent = message; el.hidden = !message; }
    }

    missingSides() {
      return this.sides.filter((side) => {
        if (side.dataset.mmRequired !== 'true') return false;
        const input = side.querySelector('[data-mm-property]');
        return !input || input.disabled || !input.value;
      });
    }

    onSubmit(event) {
      const missing = this.missingSides();
      if (!missing.length) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      missing.forEach((side) => this.setError(side, side.dataset.mmError));
      this.openStep(missing[0].dataset.mmSide);
      const submit = this.form.querySelector('[type="submit"], button:not([type])');
      if (window.theme && typeof theme.showQuickPopup === 'function' && themeJQ() && submit) {
        theme.showQuickPopup(missing[0].dataset.mmError, themeJQ()(submit));
      }
    }

    // ----- expand modal: shows the current category's swatches at full size
    initModal() {
      if (!this.modal) return;
      this.modal.querySelector('[data-mm-modal-close]').addEventListener('click', () => this.closeModal());
      this.modal.querySelector('[data-mm-modal-done]').addEventListener('click', () => this.closeModal());
      this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.closeModal(); });
      this.modal.addEventListener('close', () => this.restoreModalPanel());
    }

    openModal(side, handle) {
      if (!this.modal) return;
      const panel = side.querySelector(`[data-mm-panel="${handle}"]`);
      const swatches = panel && panel.querySelector('.mix-match-swatches');
      if (!swatches) return;
      // Move the real radio group into the dialog so selection state is shared, then put it back on close.
      this.modalOrigin = { parent: swatches.parentNode, next: swatches.nextSibling, swatches, side };
      const body = this.modal.querySelector('[data-mm-modal-body]');
      body.innerHTML = '';
      body.appendChild(swatches);
      swatches.classList.add('mix-match-swatches--modal');
      this.modal.querySelector('[data-mm-modal-title]').textContent = `${side.querySelector('.mix-match-step__title').textContent.trim()} · ${panel.querySelector('.mix-match-option__label').firstChild.textContent.trim()}`;
      this.updateModalSelection(side);
      this.modalSelectionHandler = () => this.updateModalSelection(side);
      swatches.addEventListener('change', this.modalSelectionHandler);
      if (typeof this.modal.showModal === 'function') this.modal.showModal(); else this.modal.setAttribute('open', '');
    }

    updateModalSelection(side) {
      const el = this.modal.querySelector('[data-mm-modal-selection]');
      const checked = side.querySelector('[data-mm-design]:checked');
      el.textContent = checked ? `${checked.value} · ${checked.dataset.mmCategoryName || ''}` : '';
    }

    restoreModalPanel() {
      const o = this.modalOrigin;
      if (!o) return;
      o.swatches.classList.remove('mix-match-swatches--modal');
      o.swatches.removeEventListener('change', this.modalSelectionHandler);
      o.parent.insertBefore(o.swatches, o.next);
      this.modalOrigin = null;
    }

    closeModal() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function' && this.modal.open) this.modal.close(); else { this.modal.removeAttribute('open'); this.restoreModalPanel(); }
    }
  }

  customElements.define('mix-match-picker', MixMatchPicker);
})();
